import React from "react";
import {TravelRequirements} from "../../models/RoundTripFlightMatch";
import {useTranslation} from "../../i18n/LanguageProvider";

// Schengen membership: https://www.consilium.europa.eu/en/policies/schengen-area/
// Includes Bulgaria and Romania since 2025; Ireland and Cyprus are not members.
const SCHENGEN_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IS", "IT", "LV", "LI", "LT", "LU", "MT", "NL", "NO", "PL", "PT", "RO",
  "SK", "SI", "ES", "SE", "CH",
]);

export default function TravelRequirementsBadge({requirements}: {requirements?: TravelRequirements | null}) {
  const {t, locale} = useTranslation();
  if (!requirements) return null;
  const label = requirements.status;
  const color = (label === "visa_free" || label === "entry_right_assumed") ? "bg-emerald-50 text-emerald-800" :
    (label === "visa_required" || label === "restricted") ? "bg-red-50 text-red-700" :
    label === "unknown" ? "bg-gray-100 text-gray-600" : "bg-orange-50 text-orange-700";
  const names = new Intl.DisplayNames([locale], {type: "region"});
  const destination = requirements.destination_country?.toUpperCase();
  const statusText = label === "visa_required" && destination
    ? SCHENGEN_COUNTRIES.has(destination)
      ? t("visa.status.schengen")
      : t("visa.status.countryVisa", {country: names.of(destination) || destination})
    : t(`visa.status.${label}`);
  return (
    <details className="relative my-2 min-w-0 text-left text-xs">
      <summary className={`cursor-pointer rounded-lg px-3 py-2 font-semibold ${color}`}>
        {statusText}
      </summary>
      <div className="mt-2 max-w-sm space-y-2 rounded-xl border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-700 shadow-sm">
        <p className="font-semibold">{t("visa.passport")}: {names.of(requirements.passport_country) || requirements.passport_country}</p>
        {requirements.departure_country && <p>{t("visa.departureAssumption", {country: names.of(requirements.departure_country) || requirements.departure_country})}</p>}
        {requirements.max_days !== null && <p>{t("visa.days", {days: requirements.max_days})}</p>}
        {requirements.review_reasons.map(reason => <p key={reason}>{t(`visa.reason.${reason}`)}</p>)}
        <p>{t("visa.scope")}</p>
        <p>{t("visa.updated", {date: requirements.data_updated_at})}</p>
        <a href={requirements.source_url} target="_blank" rel="noopener noreferrer" className="inline-block text-accent underline">{t("visa.source")}</a>
        <a href="https://www.iata.org/en/travel-centre/" target="_blank" rel="noopener noreferrer" className="ml-3 inline-block text-accent underline">{t("visa.check")}</a>
      </div>
    </details>
  );
}
